import React from 'react';
import { CalendarDays } from 'lucide-react';
import { groupByEvent } from '../../lib/groupByEvent';

export interface StatusTone {
    label: string;
    className: string;
}

export interface TrackingColumn<T> {
    key: string;
    header: string;
    render: (row: T) => React.ReactNode;
    className?: string;
}

export interface TrackingGroupInfo {
    key: string;
    label: string;
    date: string;
}

interface StatusTrackingTableProps<T> {
    columns: TrackingColumn<T>[];
    rows: T[];
    getRowKey: (row: T) => string;
    getStatus: (row: T) => StatusTone;
    renderActions?: (row: T) => React.ReactNode;
    onRowClick?: (row: T) => void;
    groupBy?: (row: T) => TrackingGroupInfo | null;
    emptyMessage: string;
    // Actions au niveau du groupe (ex: bouton "Télécharger le récap" des extras confirmés d'un événement) —
    // reçoit toutes les lignes de ce groupe pour que l'appelant décide lui-même s'il a quelque chose à
    // proposer (ex: seulement si au moins une ligne est confirmée). Demande d'Emeni (2026-08-22).
    renderGroupActions?: (rows: T[]) => React.ReactNode;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

// Table de suivi générique — remplace la navigation par onglets de Commandes/Extras par un tableau continu
// avec une colonne statut (pastille) et un filtre (StatusFilterChips) au-dessus. Le regroupement optionnel
// (groupBy) réutilise groupByEvent pour préserver le regroupement par privatisation côté Extras : une ligne
// d'en-tête de groupe (colSpan) est insérée avant chaque groupe, les lignes sans groupe suivent sans en-tête.
export function StatusTrackingTable<T>({ columns, rows, getRowKey, getStatus, renderActions, onRowClick, groupBy, emptyMessage, renderGroupActions }: StatusTrackingTableProps<T>) {
    if (rows.length === 0) {
        return <p className="text-xs text-content-muted">{emptyMessage}</p>;
    }

    const colCount = columns.length + 1 + (renderActions ? 1 : 0);

    const renderRow = (row: T) => (
        <tr key={getRowKey(row)} onClick={() => onRowClick?.(row)}
            className={onRowClick ? 'cursor-pointer hover:bg-surface-soft transition-colors' : ''}>
            {columns.map((col) => (
                <td key={col.key} className={`px-3 py-2.5 align-top text-sm text-content ${col.className || ''}`}>{col.render(row)}</td>
            ))}
            <td className="px-3 py-2.5 align-top">
                {(() => {
                    const status = getStatus(row);
                    return <span className={`inline-flex text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest whitespace-nowrap ${status.className}`}>{status.label}</span>;
                })()}
            </td>
            {renderActions && (
                <td className="px-3 py-2.5 align-top" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5 flex-wrap">{renderActions(row)}</div>
                </td>
            )}
        </tr>
    );

    const groupHeaderRow = (key: string, label: string, date: string, items: T[]) => (
        <tr key={`group-${key}`} className="bg-brand-500/10">
            <td colSpan={colCount} className="px-3 py-1.5">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-black text-brand-600 dark:text-brand-400 uppercase tracking-widest">{label || 'Sans privatisation'}</span>
                        {date && <span className="text-[10px] text-content-muted flex items-center gap-1"><CalendarDays size={11} /> {fmtDate(date)}</span>}
                    </div>
                    {renderGroupActions?.(items)}
                </div>
            </td>
        </tr>
    );

    let body: React.ReactNode;
    if (groupBy) {
        const { groups, ungrouped } = groupByEvent(rows, groupBy);
        body = (
            <>
                {groups.map((g) => (
                    <React.Fragment key={g.key}>
                        {groupHeaderRow(g.key, g.label, g.date, g.items)}
                        {g.items.map(renderRow)}
                    </React.Fragment>
                ))}
                {ungrouped.map(renderRow)}
            </>
        );
    } else {
        body = rows.map(renderRow);
    }

    return (
        <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full border-collapse">
                <thead>
                    <tr className="bg-surface-soft border-b border-border">
                        {columns.map((col) => (
                            <th key={col.key} className={`px-3 py-2 text-left text-[10px] font-black text-content-muted uppercase tracking-widest ${col.className || ''}`}>{col.header}</th>
                        ))}
                        <th className="px-3 py-2 text-left text-[10px] font-black text-content-muted uppercase tracking-widest">Statut</th>
                        {renderActions && <th className="px-3 py-2 text-left text-[10px] font-black text-content-muted uppercase tracking-widest">Actions</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">{body}</tbody>
            </table>
        </div>
    );
}
